import React, { useLayoutEffect, useRef, useState } from 'react';
import { formatDatum, formatZahl } from '../utils/format';

// Fallback-Breite, bis die erste Messung per ResizeObserver vorliegt (siehe
// unten) – verhindert nur einen kurzen Layout-Sprung beim ersten Rendern.
const VB_WIDTH_FALLBACK = 600;

/** Rundet eine Schrittweite auf 1/2/5 * 10^n – für „glatte" Achsenwerte. */
function niceStep(rawStep) {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const norm = rawStep / magnitude;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * magnitude;
}

function buildYTicks(min, max, targetCount = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { ticks: [0, 1], domainMin: 0, domainMax: 1 };
  let lo = min;
  let hi = max;
  if (lo === hi) { lo -= 1; hi += 1; }
  const step = niceStep((hi - lo) / targetCount);
  const domainMin = Math.floor(lo / step) * step;
  const domainMax = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = domainMin; v <= domainMax + step / 2; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return { ticks, domainMin, domainMax: domainMax === domainMin ? domainMin + step : domainMax };
}

/** `count` gleichmäßig verteilte Zeitpunkte zwischen minT und maxT (inklusive Rand). */
function buildXTicks(minT, maxT, count = 5) {
  if (!Number.isFinite(minT) || !Number.isFinite(maxT)) return [];
  if (minT === maxT) return [minT];
  const ticks = [];
  for (let i = 0; i < count; i += 1) {
    ticks.push(minT + ((maxT - minT) * i) / (count - 1));
  }
  return ticks;
}

/**
 * Verlaufsdiagramm für Einzelergebnisse (Ringe je Wettkampf) über die Zeit –
 * eine Linie je Serie (Wettkampfart), alle auf derselben Ringe-Achse.
 *
 * Die X-Achse bildet den echten Zeitverlauf ab (proportional zum Datum, nicht
 * zur Reihenfolge) – Pausen zwischen Saisons erscheinen dadurch als Lücke,
 * dicht aufeinanderfolgende Wettkämpfe rücken zusammen.
 *
 * `series`: [{ key, label, color, data: [{ datum, ringe, subtitle? }] }, …]
 * Die Farbe kommt bewusst vom Aufrufer (feste, nie rotierende Zuordnung je
 * Wettkampfart – siehe DashboardPage) statt hier neu vergeben zu werden.
 */
function ErgebnisHistorieChart({ series, height = 240, valueLabel = 'Ringe' }) {
  const containerRef = useRef(null);
  const [hoverKey, setHoverKey] = useState(null);
  // Isolierte Serie nach Klick auf die Legende – null zeigt alle. Ist der
  // gemerkte Schlüssel in den aktuellen Daten nicht mehr vorhanden (z. B. nach
  // Wechsel des Zeitraum-Filters), gilt das wie "alle anzeigen".
  const [focusedKey, setFocusedKey] = useState(null);

  // Tatsächliche Containerbreite in Pixeln – wird 1:1 als viewBox-Breite
  // verwendet, damit Text/Linien nicht horizontal verzerrt werden (relevant
  // vor allem auf schmalen Handy-Bildschirmen).
  const [measuredWidth, setMeasuredWidth] = useState(0);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const updateWidth = () => setMeasuredWidth(el.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const vbWidth = measuredWidth || VB_WIDTH_FALLBACK;

  const seriesList = (Array.isArray(series) ? series : [])
    .map((s) => ({
      ...s,
      points: (Array.isArray(s.data) ? s.data : [])
        .map((d) => ({ ...d, t: new Date(d.datum).getTime() }))
        .filter((d) => Number.isFinite(d.t) && Number.isFinite(Number(d.ringe)))
        .sort((a, b) => a.t - b.t),
    }))
    .filter((s) => s.points.length > 0);

  const totalPoints = seriesList.reduce((sum, s) => sum + s.points.length, 0);

  if (totalPoints === 0) {
    return <p className="text-sm text-slate-400 italic py-10 text-center">Keine Ergebnisse vorhanden.</p>;
  }

  const effectiveFocusedKey = seriesList.some((s) => s.key === focusedKey) ? focusedKey : null;
  const aktiveSeriesList = effectiveFocusedKey ? seriesList.filter((s) => s.key === effectiveFocusedKey) : seriesList;

  const padding = { top: 16, right: 12, bottom: 26, left: 42 };
  const plotWidth = vbWidth - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const aktiveWerte = aktiveSeriesList.flatMap((s) => s.points.map((p) => Number(p.ringe)));
  const { ticks, domainMin, domainMax } = buildYTicks(Math.min(...aktiveWerte), Math.max(...aktiveWerte));
  // Ergebnisse werden im Dashboard immer als Ganzzahl angezeigt.
  const tickDecimals = 0;

  const aktiveZeiten = aktiveSeriesList.flatMap((s) => s.points.map((p) => p.t));
  const minT = Math.min(...aktiveZeiten);
  const maxT = Math.max(...aktiveZeiten);

  const xAt = (t) => (minT === maxT ? padding.left + plotWidth / 2 : padding.left + ((t - minT) / (maxT - minT)) * plotWidth);
  const yAt = (v) => {
    const range = domainMax - domainMin || 1;
    return padding.top + plotHeight - ((v - domainMin) / range) * plotHeight;
  };

  const plotted = aktiveSeriesList.map((s) => {
    const pts = s.points.map((p, idx) => ({ ...p, idx, x: xAt(p.t), y: yAt(Number(p.ringe)) }));
    return {
      ...s,
      pts,
      pathD: pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' '),
      lastPt: pts[pts.length - 1],
    };
  });

  // Weniger Ticks auf schmalen Bildschirmen, damit sich Datumsbeschriftungen
  // (z. B. "18.09.2026") nicht überlappen – Richtwert ~80px pro Beschriftung.
  const maxXTicksByWidth = Math.max(2, Math.min(6, Math.floor(vbWidth / 80)));
  const xTicks = buildXTicks(minT, maxT, Math.min(maxXTicksByWidth, aktiveZeiten.length));

  const handleLegendClick = (key) => setFocusedKey((prev) => (prev === key ? null : key));

  const updateHoverFromClientX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const fracX = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const xUnit = fracX * vbWidth;
    let nearest = null;
    let bestDist = Infinity;
    plotted.forEach((s) => {
      s.pts.forEach((p) => {
        const dist = Math.abs(p.x - xUnit);
        if (dist < bestDist) { bestDist = dist; nearest = { seriesKey: s.key, point: p, color: s.color, label: s.label }; }
      });
    });
    setHoverKey(nearest);
  };

  const handleMouseMove = (evt) => updateHoverFromClientX(evt.clientX);
  const handleTouch = (evt) => {
    const touch = evt.touches?.[0];
    if (touch) updateHoverFromClientX(touch.clientX);
  };
  const handleLeave = () => setHoverKey(null);

  const showLegend = seriesList.length > 1;

  return (
    <div>
      {showLegend && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5 mb-3">
          {seriesList.map((s) => {
            const aktiv = !effectiveFocusedKey || s.key === effectiveFocusedKey;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => handleLegendClick(s.key)}
                title={s.key === effectiveFocusedKey ? 'Klicken, um alle Disziplinen anzuzeigen' : 'Klicken, um nur diese Disziplin anzuzeigen'}
                className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-xs transition-colors ${
                  aktiv ? 'text-slate-700 hover:bg-slate-100' : 'text-slate-300 hover:bg-slate-50 hover:text-slate-400'
                } ${s.key === effectiveFocusedKey ? 'bg-slate-100 font-medium' : ''}`}
              >
                <span className="inline-block h-0.5 w-4 rounded-full shrink-0" style={{ backgroundColor: aktiv ? s.color : '#cbd5e1' }} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      {/* Infoleiste oberhalb des Diagramms statt Tooltip im Plotbereich –
          so verdeckt die Anzeige nie andere Punkte (oder den Finger-Bereich
          auf dem Handy). Die Höhe ist fest reserviert, damit das Diagramm
          beim Hovern nicht springt. */}
      <div className="relative h-14 mb-1 text-xs">
        {hoverKey ? (() => {
          const frac = hoverKey.point.x / vbWidth;
          const align = frac < 0.2 ? 'translate-x-0' : frac > 0.8 ? '-translate-x-full' : '-translate-x-1/2';
          return (
            <div
              className={`pointer-events-none absolute top-0 ${align} rounded-lg border border-slate-200 bg-white px-2.5 py-1 shadow-sm whitespace-nowrap text-center`}
              style={{ left: `${frac * 100}%` }}
            >
              <div className="flex items-center justify-center gap-1.5">
                <span className="inline-block h-0.5 w-3 rounded-full shrink-0" style={{ backgroundColor: hoverKey.color }} />
                <span className="font-semibold text-slate-900">{formatZahl(hoverKey.point.ringe, 0)} {valueLabel}</span>
                <span className="text-slate-500">{formatDatum(hoverKey.point.datum)}</span>
              </div>
              {hoverKey.label && <div className="text-slate-500">{hoverKey.label}</div>}
              {hoverKey.point.subtitle && <div className="text-slate-400">{hoverKey.point.subtitle}</div>}
            </div>
          );
        })() : (
          <p className="pt-4 text-center text-slate-300">Punkt antippen oder überfahren für Details</p>
        )}
      </div>
      <div ref={containerRef} className="relative select-none" style={{ touchAction: 'pan-y' }}>
        <svg
          viewBox={`0 0 ${vbWidth} ${height}`}
          width="100%"
          height={height}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleLeave}
          onTouchStart={handleTouch}
          onTouchMove={handleTouch}
          onTouchEnd={handleLeave}
          role="img"
          aria-label={`Verlauf der ${valueLabel} über ${totalPoints} Wettkämpfe`}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={padding.left} x2={vbWidth - padding.right}
                y1={yAt(t)} y2={yAt(t)}
                className="stroke-slate-200"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={padding.left - 6} y={yAt(t)}
                textAnchor="end" dominantBaseline="middle"
                className="fill-slate-400"
                style={{ fontSize: 9 }}
              >
                {formatZahl(t, tickDecimals)}
              </text>
            </g>
          ))}

          {xTicks.map((t, i) => (
            <text
              key={t}
              x={xAt(t)} y={height - padding.bottom + 16}
              textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
              className="fill-slate-400"
              style={{ fontSize: 9 }}
            >
              {formatDatum(t)}
            </text>
          ))}

          {hoverKey && (
            <line
              x1={hoverKey.point.x} x2={hoverKey.point.x}
              y1={padding.top} y2={height - padding.bottom}
              className="stroke-slate-300"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          )}

          {plotted.map((s) => (
            <g key={s.key}>
              <path
                d={s.pathD} fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round" strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              {s.pts.map((p) => {
                // Index statt Datum als Schlüssel: mehrere Ergebnisse am selben
                // Tag würden sonst doppelte React-Keys erzeugen, wodurch beim
                // Neuzeichnen verwaiste Punkte ohne Linie stehen bleiben.
                const isHover = hoverKey?.seriesKey === s.key && hoverKey.point.idx === p.idx;
                const isLast = p.idx === s.lastPt.idx;
                const emphasized = isHover || isLast;
                return (
                  <circle
                    key={p.idx}
                    cx={p.x} cy={p.y}
                    r={emphasized ? 5 : 2.5}
                    fill={s.color}
                    stroke="#fff"
                    strokeWidth={emphasized ? 2 : 1}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            </g>
          ))}
        </svg>

      </div>
    </div>
  );
}

export default ErgebnisHistorieChart;
